<script setup>
// Addendum §2.3 LifecycleButton — a lifecycle action with friction gated by
// BLAST RADIUS, not reversibility (everything here is reversible).
//
//   verb                                    friction
//   close / restore / reopen / reactivate   optimistic undo toast, 5s (no modal)
//   deactivate (user)                        confirm modal (kills sessions, frees a seat)
//   archive (client)                         confirm modal naming the cascade
//   remove-access                            type-to-confirm
//
// Nothing reversible renders red — only `remove-access` is destructive. Red on
// a harmless action trains users to ignore red.
//
// Events:
//   commit  — the action should actually happen. For undo-class verbs this
//             fires OPTIMISTICALLY; the host applies it immediately.
//   undo    — the 5s window was used. Roll back the TRANSACTION (do not fire a
//             compensating reopen — that would pollute reopen_count, §2.3).
import { ref, computed } from 'vue'
import LButton from './Button.vue'
import LModal from '../surfaces/Modal.vue'
import LToast from '../surfaces/Toast.vue'
import LInputField from '../inputs/InputField.vue'

const props = defineProps({
  verb: { type: String, required: true },
  objectLabel: { type: String, default: 'item' }, // firm-configurable noun
  objectName: { type: String, default: null }, // proper name for messages
  cascade: { type: Object, default: null }, // { count, noun } for archive
  size: { type: String, default: 'sm' },
})
const emit = defineEmits(['commit', 'undo'])

const FRICTION = {
  close: 'undo',
  restore: 'undo',
  reopen: 'undo',
  reactivate: 'undo',
  deactivate: 'confirm',
  archive: 'confirm',
  'remove-access': 'type-confirm',
}
const PAST = {
  close: 'Closed',
  restore: 'Restored',
  reopen: 'Reopened',
  reactivate: 'Reactivated',
  deactivate: 'Deactivated',
  archive: 'Archived',
  'remove-access': 'Access removed',
}
const friction = computed(() => FRICTION[props.verb] ?? 'confirm')
const isDestructive = computed(() => props.verb === 'remove-access')

const label = computed(() => {
  const cap = props.verb.charAt(0).toUpperCase() + props.verb.slice(1).replace('-', ' ')
  if (props.verb === 'remove-access') return 'Remove access'
  if (props.verb === 'archive' && props.objectName) return `Archive ${props.objectName}`
  return `${cap} ${props.objectLabel}`
})
const named = computed(() => props.objectName || props.objectLabel)

// --- confirm modal copy ---------------------------------------------------
const confirmTitle = computed(() => {
  if (props.verb === 'archive' && props.cascade) {
    return `Archive ${named.value} and close ${props.cascade.count} ${props.cascade.noun}?`
  }
  if (props.verb === 'deactivate') return `Deactivate ${named.value}?`
  return `${label.value}?`
})
const confirmBody = computed(() => {
  if (props.verb === 'deactivate') {
    return 'This ends their active sessions and frees a seat. Time already captured is kept. You can reactivate any time — but undo cannot un-end a session that has already been killed.'
  }
  if (props.verb === 'archive') {
    return 'Archived objects stay queryable behind a "Show archived" filter — they are never removed from results, only defaulted out. You can restore any time.'
  }
  return 'You can reverse this later.'
})

// --- state ----------------------------------------------------------------
const modalOpen = ref(false)
const toastOpen = ref(false)
const typed = ref('')
const typeMatches = computed(() => typed.value.trim() === named.value)

function onClick() {
  if (friction.value === 'undo') {
    emit('commit') // optimistic
    toastOpen.value = true
  } else {
    typed.value = ''
    modalOpen.value = true
  }
}
function confirm() {
  emit('commit')
  modalOpen.value = false
}
function undo() {
  emit('undo')
  toastOpen.value = false
}
</script>

<template>
  <span class="l-lifecycle">
    <LButton :size="size" :variant="isDestructive ? 'destructive' : 'secondary'" @click="onClick">
      {{ label }}
    </LButton>

    <!-- confirm / cascade modal -->
    <LModal
      v-if="friction === 'confirm'"
      v-model:open="modalOpen"
      :title="confirmTitle"
      :description="confirmBody"
    >
      <template #footer>
        <LButton variant="secondary" @click="modalOpen = false">Cancel</LButton>
        <LButton :variant="verb === 'deactivate' ? 'destructive' : 'primary'" @click="confirm">
          {{ label }}
        </LButton>
      </template>
    </LModal>

    <!-- type-to-confirm modal -->
    <LModal
      v-else-if="friction === 'type-confirm'"
      v-model:open="modalOpen"
      title="Remove access?"
      :description="`This terminates ${named}'s sessions across every surface immediately. It is reversible, but they will be signed out everywhere right now.`"
    >
      <LInputField
        v-model="typed"
        :label="`Type “${named}” to confirm`"
        :placeholder="named"
      />
      <template #footer>
        <LButton variant="secondary" @click="modalOpen = false">Cancel</LButton>
        <LButton variant="destructive" :disabled="!typeMatches" @click="confirm">Remove access</LButton>
      </template>
    </LModal>

    <!-- optimistic undo toast, floated at document level -->
    <Teleport to="body">
      <div v-if="friction === 'undo'" class="toast-region">
        <LToast
          v-model:open="toastOpen"
          :message="`${PAST[verb]} ${objectName || objectLabel}`"
          @undo="undo"
        />
      </div>
    </Teleport>
  </span>
</template>

<style scoped>
.l-lifecycle {
  display: inline-flex;
}
</style>

<style>
/* unscoped: the teleported toast region floats bottom-centre, above everything */
.toast-region {
  position: fixed;
  left: 50%;
  bottom: var(--sp-6);
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  justify-content: center;
  pointer-events: none;
}
.toast-region > * {
  pointer-events: auto;
}
</style>

